import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const ParticleCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const ParticleSystem = ({ particleCount = 50 }) => {
  const canvasRef = useRef(null);
  const animationId = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const createParticle = () => {
      const initialAngle = Math.random() * Math.PI * 2;
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: Math.cos(initialAngle) * 0.2,
        vy: Math.sin(initialAngle) * 0.2,
        size: Math.random() * 3 + 1,
        trail: [],
        lastAngle: initialAngle
      };
    };

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(createParticle());
      }
      particlesRef.current = particles;
    };

    const updateParticles = () => {
      particles.forEach(particle => {
        // Store trail positions
        particle.trail.push({ x: particle.x, y: particle.y });
        if (particle.trail.length > 10) {
          particle.trail.shift();
        }

        // Mouse repulsion
        const dx = particle.x - mouseRef.current.x;
        const dy = particle.y - mouseRef.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const repulsionRadius = 100;

        if (distance < repulsionRadius && distance > 0) {
          const force = (repulsionRadius - distance) / repulsionRadius;
          const angle = Math.atan2(dy, dx);
          particle.vx += Math.cos(angle) * force * 0.5;
          particle.vy += Math.sin(angle) * force * 0.5;
        }

        // Apply velocity
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x <= 0 || particle.x >= canvas.width) {
          particle.vx *= -1;
          particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        }
        if (particle.y <= 0 || particle.y >= canvas.height) {
          particle.vy *= -1;
          particle.y = Math.max(0, Math.min(canvas.height, particle.y));
        }

        // Apply gentle damping but maintain minimum motion
        particle.vx *= 0.995;
        particle.vy *= 0.995;
        
        // Ensure minimum motion to keep particles floating
        const minSpeed = 0.1;
        const currentSpeed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
        if (currentSpeed < minSpeed && currentSpeed > 0.01) {
          // Gradually increase speed instead of sudden direction change
          const currentAngle = Math.atan2(particle.vy, particle.vx);
          particle.vx = Math.cos(currentAngle) * minSpeed;
          particle.vy = Math.sin(currentAngle) * minSpeed;
        } else if (currentSpeed <= 0.01) {
          // Only assign new random direction if speed is extremely low
          const angle = Math.random() * Math.PI * 2;
          particle.vx = Math.cos(angle) * minSpeed;
          particle.vy = Math.sin(angle) * minSpeed;
        }
      });
    };

    const drawAirplane = (x, y, size, angle) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.scale(size, size);
      
      // Draw airplane shape
      ctx.beginPath();
      ctx.fillStyle = '#3b82f6';
      ctx.shadowColor = '#3b82f6';
      ctx.shadowBlur = 8;
      
      // Airplane body (fuselage)
      ctx.moveTo(-8, 0);
      ctx.lineTo(8, 0);
      ctx.lineTo(6, -1);
      ctx.lineTo(-6, -1);
      ctx.closePath();
      ctx.fill();
      
      // Wings
      ctx.beginPath();
      ctx.moveTo(-2, -1);
      ctx.lineTo(-2, -6);
      ctx.lineTo(2, -6);
      ctx.lineTo(2, -1);
      ctx.closePath();
      ctx.fill();
      
      ctx.beginPath();
      ctx.moveTo(-2, 1);
      ctx.lineTo(-2, 6);
      ctx.lineTo(2, 6);
      ctx.lineTo(2, 1);
      ctx.closePath();
      ctx.fill();
      
      // Nose
      ctx.beginPath();
      ctx.moveTo(8, 0);
      ctx.lineTo(12, 0);
      ctx.lineTo(10, -1);
      ctx.lineTo(10, 1);
      ctx.closePath();
      ctx.fill();
      
      ctx.shadowBlur = 0;
      ctx.restore();
    };

    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        // Draw trail
        if (particle.trail.length > 1) {
          ctx.beginPath();
          ctx.moveTo(particle.trail[0].x, particle.trail[0].y);
          
          for (let i = 1; i < particle.trail.length; i++) {
            const alpha = i / particle.trail.length;
            ctx.strokeStyle = `rgba(59, 130, 246, ${alpha * 0.3})`;
            ctx.lineWidth = particle.size * alpha;
            ctx.lineTo(particle.trail[i].x, particle.trail[i].y);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(particle.trail[i].x, particle.trail[i].y);
          }
        }

        // Calculate rotation angle based on velocity direction, with stability check
        const currentSpeed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
        const angle = currentSpeed > 0.05 ? Math.atan2(particle.vy, particle.vx) : (particle.lastAngle || 0);
        particle.lastAngle = angle;
        
        // Draw airplane
        drawAirplane(particle.x, particle.y, particle.size * 0.3, angle);
      });
    };

    const animate = () => {
      updateParticles();
      drawParticles();
      animationId.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
    };

    const handleResize = () => {
      resizeCanvas();
      initParticles();
    };

    // Initialize
    resizeCanvas();
    initParticles();
    animate();

    // Event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
      }
    };
  }, []);

  return <ParticleCanvas ref={canvasRef} />;
};

export default ParticleSystem;